import type { IKeyObjective } from 'legacy/lut/classes/models/partner360/IFormOptionsJBP'
import { IKIBusinessPriority } from 'legacy/lut/features/partner360/jbpSettings/models/kiBusinessPriority'

import { TKeyInitiative } from 'partner360/entities/keyInitiatives'
import {
  getGoalField,
  getInitiativeIncrementalField,
  getInitiativesPlanQuality,
  getPlanField,
  getPrevYearActualsField,
  TKeyInitiativesFinancialData,
} from 'partner360/features/keyInitiatives'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import type {
  CockpitPeriodKey,
  ICockpitKeyInitiative,
  ICockpitPlanningPeriod,
} from '../../model/types'

import { getInitiativePeriodAllocation } from './getCockpitInitiativesInScope'
import { getInitiativeMetricAmount } from './getInitiativeMetricAmount'

interface IParams {
  initiatives: ICockpitKeyInitiative[]
  businessPriorities: IKIBusinessPriority[]
  keyObjectives?: readonly IKeyObjective[]
  selectedMetric: ActualsValueTypes
  selectedCurrency: string
  selectedPeriods: CockpitPeriodKey[]
  planningPeriod: ICockpitPlanningPeriod | null
  /** This year's target, as the Revenue & SOB tile computes it. */
  planSales: number | null
  /** Last year's actuals — the baseline the incremental target is measured from. */
  baselineSales: number | null
}

/**
 * The Initiatives plan quality of the Planning activities widget, computed from cockpit data.
 *
 * The calculation itself is the widget's own `getInitiativesPlanQuality` — the three pillars must
 * score the same in both places, so the rule is not restated here. Only its two inputs are
 * adapted:
 *
 * - it reads the plan and the prior-year actuals off one **wide** Fin/SOB record
 *   (`PlansCY…` / `ActualsPY…`), while the cockpit holds long-format rows. The two figures are
 *   exactly what `useCockpitBaselineAndPlan` already derives from them, so they are handed over
 *   through a record carrying just those two measures, keyed by the same field helpers the
 *   calculation looks them up with;
 * - initiatives come from `planningCockpitGzip` rather than the initiatives query. It is the same
 *   entity, and the calculation reads only fields the cockpit payload carries. Goal and
 *   Incremental are adapted to the selected quarters' equal shares; the remaining fields are
 *   passed through unchanged.
 */
export const getCockpitInitiativesQuality = ({
  initiatives,
  businessPriorities,
  keyObjectives,
  selectedMetric,
  selectedCurrency,
  selectedPeriods,
  planningPeriod,
  planSales,
  baselineSales,
}: IParams) => {
  const finRecord: TKeyInitiativesFinancialData = {
    [getPlanField(selectedMetric, selectedCurrency)]: planSales ?? 0,
    [getPrevYearActualsField(selectedMetric, selectedCurrency)]: baselineSales ?? 0,
  }

  const isLocalCurrency = selectedCurrency !== 'USD'
  const goalField = getGoalField(selectedMetric, selectedCurrency)
  const incrementalField = getInitiativeIncrementalField(selectedMetric, selectedCurrency)
  const adjustedInitiatives = initiatives.map((initiative) => {
    const allocation = getInitiativePeriodAllocation(
      initiative.Period,
      selectedPeriods,
      planningPeriod?.fQuarter
    )
    const allocate = (value: number | null) => (value === null ? undefined : value * allocation)

    return {
      ...initiative,
      [goalField]: allocate(
        getInitiativeMetricAmount(initiative, 'Goal', selectedMetric, isLocalCurrency)
      ),
      [incrementalField]: allocate(
        getInitiativeMetricAmount(initiative, 'Incremental', selectedMetric, isLocalCurrency)
      ),
    } as unknown as TKeyInitiative
  })

  return getInitiativesPlanQuality({
    data: adjustedInitiatives,
    finData: [finRecord],
    businessPriorities,
    keyObjectives,
    activeCurrency: selectedCurrency,
    actualsType: selectedMetric,
  })
}

export type TCockpitInitiativesQuality = ReturnType<typeof getCockpitInitiativesQuality>
